import { useState, useRef } from 'react'
import { string, node } from 'prop-types'
import Link from 'next/link'
import styled from 'styled-components'
import CloudinaryImage from '@components/Image/CloudinaryImage'

import { cmsPageUrl, px2rem, mq } from '@utils'
import { Text, SlickCarousel, ImageRect } from '@components'
import cssClasses from '@constants/cssClasses'
import CallToAction from '@reusable-modules/common/CallToAction'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import { Col, Container, Icon, Row } from '@mch-group/uikit-components'

const mID = 'rm-carousel'

const RmCarousel = (props) => {
  const sliderRef = useRef(null)
  const [currentSlideCount, setCurrentSlideCount] = useState(1)
  const formattedObject = Array.from({ length: 20 })
    .map((i, index) => ({
      index,
      image: props[`carousel_grp${index + 1}_image`],
      caption: props[`carousel_grp${index + 1}_caption`],
      order: props[`carousel_grp${index + 1}_order`],
      subtitle: props[`carousel_grp${index + 1}_subtitle`],
      body: props[`carousel_grp${index + 1}_body`],
      ctaLabel: props[`carousel_grp${index + 1}_cta_label`],
      ctaHref: props[`carousel_grp${index + 1}_cta_href`]
    }))
    .filter(({ image }) => typeof image !== 'undefined') // Filtering undefined images
    .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sorting by "order" field

  // Checking which is the right layout (large or small image).
  // If there is not even one subtitle, body, ctaLabel or ctaHref, show large image layout
  const isLargeImageLayout = Object.entries(formattedObject).every(
    (value) =>
      typeof value[1].subtitle === 'undefined' &&
      typeof value[1].body === 'undefined' &&
      typeof value[1].ctaLabel === 'undefined' &&
      typeof value[1].ctaHref === 'undefined'
  )

  const goPrevSlide = () => {
    // @ts-ignore
    sliderRef.current.slickPrev()
    const slideCount = currentSlideCount === 1 ? formattedObject.length : currentSlideCount - 1
    setCurrentSlideCount(slideCount)
  }

  const goNextSlide = () => {
    // @ts-ignore
    sliderRef.current.slickNext()
    const slideCount = currentSlideCount === formattedObject.length ? 1 : currentSlideCount + 1
    setCurrentSlideCount(slideCount)
  }
  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    afterChange: (index) => {
      setCurrentSlideCount(index + 1) // this change for swipe image slide and update pagination count
    }
  }
  return (
    <Container className={`${props.className} ${cssClasses.CMS_MODULE} px-5 px-lg-8 px-md-7`}>
      <SlickCarousel
        settings={sliderSettings}
        setSliderRef={(ref) => {
          if (!sliderRef.current) {
            sliderRef.current = ref
          }
        }}
      >
        {formattedObject.map((item) => (
          <div key={`${props.id}-${item.index}`}>
            <Row stretched>
              <Col md={isLargeImageLayout ? 10 : 6} xs={12}>
                <figure className={`${mID}__left-column`}>
                  {/* @ts-ignore */}
                  <ImageRect aspectRatio='16:10' outer>
                    <CloudinaryImage
                      publicId={item.image}
                      crop='fill'
                      alt={item.caption}
                      responsive
                      dpr='auto'
                      f='auto'
                      q='auto'
                    />
                  </ImageRect>
                  {isLargeImageLayout && <figcaption className={`${mID}__caption`}>{item.caption}</figcaption>}
                </figure>
              </Col>
              <Col md={6} xs={12}>
                <div className={`${mID}__right-column`}>
                  {/* @ts-ignore */}
                  <ArticleLinkWrapper href={item.ctaHref}>
                    {!isLargeImageLayout && (
                      <header>
                        {item.caption && <h3 className='h3'>{item.caption}</h3>}
                        {item.subtitle && <h4 className='text-paragraph-2'>{item.subtitle}</h4>}
                      </header>
                    )}
                    {item.body && <Text isHtml>{item.body}</Text>}
                  </ArticleLinkWrapper>
                  {item.ctaHref && (
                    <CallToAction href={cmsPageUrl(item.ctaHref)} as={item.ctaHref}>
                      {item.ctaLabel}
                    </CallToAction>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        ))}
      </SlickCarousel>

      {formattedObject.length > 1 && (
        <Row className='slick__navigation'>
          <Col md={isLargeImageLayout ? 10 : 12} xs={12}>
            <div className='slick__navigation-controls d-flex'>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a onClick={goPrevSlide} onKeyUp={goPrevSlide} role='link' tabIndex={0}>
                <Icon name='chevron-left' />
              </a>
              <Text isSmall className='slick__navigation-controls-num'>
                <strong>{currentSlideCount}</strong>
                {' / '}
                {formattedObject.length}
              </Text>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a onClick={goNextSlide} onKeyUp={goNextSlide} role='link' tabIndex={0}>
                <Icon name='chevron-right' />
              </a>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  )
}

RmCarousel.propTypes = {
  className: string
}

// Wraps or not the header in a link
const ArticleLinkWrapper = ({ children, href }) =>
  href ? (
    <Link href={href} legacyBehavior>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a>{children}</a>
    </Link>
  ) : (
    [children]
  )

ArticleLinkWrapper.propTypes = {
  children: node,
  href: string
}

const RmCarouselStyled = styled(RmCarousel)`
  ${mq.between('tablet', 'largestTablet')`
    padding-bottom: ${px2rem(45)};
  `}
  ${mq.lessThan('largestMobile')`
    padding-bottom: ${px2rem(30)};
  `}

  img {
    width: 100%;
  }

  figure {
    margin: 0 !important;
  }

  .${mID} {
    &__right-column {
      display: flex;
      flex-direction: column;
      ${mq.lessThan('largestMobile')`
        padding-bottom: 80px;
      `}
      & > a {
        font-weight: ${tokens.fontWeights.ivarDisplay4.value} !important;

        &:first-of-type + .call-to-action {
          margin-top: ${px2rem(15)};
        }

        a {
          font-weight: ${tokens.fontWeights.artBaselText1.value};
        }
      }

      h3.ui.header {
        margin-bottom: ${px2rem(7)};
      }

      header {
        margin-bottom: ${px2rem(16)};
        ${mq.between('tablet', 'largestTablet')`
          margin-top: ${px2rem(32)};
          margin-bottom: ${px2rem(20)};
        `}
        ${mq.lessThan('largestMobile')`
          margin-top: ${px2rem(21)};
          margin-bottom: ${px2rem(13)};
        `}
      }
    }

    &__caption {
      display: inline-block;
      padding-top: ${px2rem(14)};
      color: ${tokens.color.light.base.neutrals['500'].value};
    }
  }
`

export default RmCarouselStyled
